import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { alpha } from '@mui/material/styles';
import PaymentIcon from '@mui/icons-material/Payment';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import CardActions from '@mui/material/CardActions';
import IconButton from '@mui/material/IconButton';
import Fade from '@mui/material/Fade';
import RtlButton from './RtlButton';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from './CustomNotification';
import { payInstallment, uploadInstallmentReceipt, type Installment, type PayInstallmentPayload, type PaymentMethodType } from '../data/orders';
import dayjs from 'dayjs';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface InstallmentPaymentDialogProps {
  open: boolean;
  installment: Installment | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function InstallmentPaymentDialog({
  open,
  installment,
  onClose,
  onSuccess,
}: InstallmentPaymentDialogProps) {
  const { t, direction } = useLanguage();
  const notifications = useNotification();
  const isRtl = direction === 'rtl';
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [paymentMethod, setPaymentMethod] = React.useState<PaymentMethodType>('CASH');
  const [notes, setNotes] = React.useState('');
  const [receiptImage, setReceiptImage] = React.useState<File | null>(null);

  React.useEffect(() => {
    if (open && installment) {
      setPaymentMethod('CASH');
      setNotes('');
      setReceiptImage(null);
    }
  }, [open, installment]);

  const handlePaymentMethodChange = (event: SelectChangeEvent) => {
    setPaymentMethod(event.target.value as PaymentMethodType);
  };

  const handleNotesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNotes(event.target.value);
  };

  const handleImageChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        setReceiptImage(files[0]);
      }
    },
    [],
  );

  const handleRemoveImage = React.useCallback(() => {
    setReceiptImage(null);
  }, []);

  const handleSubmit = async () => {
    if (!installment || !installment.id) return;

    setIsSubmitting(true);
    try {
      const payload: PayInstallmentPayload = {
        method: paymentMethod,
        ...(notes ? { notes } : {}),
      };

      // Upload receipt image first if provided
      if (receiptImage) {
        await uploadInstallmentReceipt(installment.id, receiptImage);
      }

      await payInstallment(installment.id, payload);

      notifications.show(t('orders.paymentSuccess'), {
        severity: 'success',
        autoHideDuration: 3000,
      });

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      notifications.show(
        `${t('messages.errorOccurred')}: ${(error as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!installment) {
    return null;
  }

  return (
    <Dialog
      open={open && !!installment}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{
        component: 'form',
        onSubmit: (e: React.FormEvent) => {
          e.preventDefault();
          handleSubmit();
        },
        sx: (theme) => ({
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(2, 139, 203, 0.15)',
          background: theme.palette.background.paper,
          ...theme.applyStyles('dark', {
            background: alpha('#1a1a1a', 0.95),
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
          }),
        }),
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          pt: 3,
          px: 3,
          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          fontWeight: 600,
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <PaymentIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
        {t('orders.payInstallment')}
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: 4, overflow: 'visible' }}>
        <Box
          sx={(theme) => ({
            mb: 3,
            p: 2.5,
            marginTop: 3,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}08, ${theme.palette.primary.light}15)`,
            border: '2px solid',
            borderColor: 'primary.light',
            ...theme.applyStyles('dark', {
              background: alpha('#028bcb', 0.1),
              borderColor: alpha('#028bcb', 0.3),
            }),
          })}
        >
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
            {t('orders.dueDate')}: {dayjs(installment.due_date).format('YYYY-MM-DD')}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              mt: 1,
            }}
          >
            {t('orders.amount')}: {((typeof installment.amount === 'string'
              ? parseFloat(installment.amount)
              : installment.amount) || 0).toFixed(2)} {direction === 'rtl' ? 'د.إ' : 'AED'}
          </Typography>
        </Box>
        <Grid container spacing={3} sx={{ mt: 0 }}>
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth required>
              <InputLabel
                sx={{
                  textAlign: isRtl ? 'right' : 'left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0,
                  fontWeight: 500,
                }}
              >
                {t('orders.paymentMethod')}
              </InputLabel>
              <Select
                value={paymentMethod}
                onChange={handlePaymentMethodChange}
                label={t('orders.paymentMethod')}
                sx={{
                  backgroundColor: 'transparent',
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(2, 139, 203, 0.1)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                }}
              >
                <MenuItem value="CASH">{t('orders.cash')}</MenuItem>
                <MenuItem value="CARD">{t('orders.card')}</MenuItem>
                <MenuItem value="BANK_TRANSFER">{t('orders.bankTransfer')}</MenuItem>
                <MenuItem value="CHEQUE">{t('orders.cheque')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label={t('orders.notes')}
              fullWidth
              multiline
              value={notes}
              onChange={handleNotesChange}
              InputLabelProps={{
                sx: {
                  textAlign: isRtl ? 'right' : 'left',
                  right: isRtl ? 0 : 'auto',
                  left: isRtl ? 'auto' : 0,
                  fontWeight: 500
                }
              }}
              sx={{
                  '& .MuiInputBase-root': {
                      alignItems: 'flex-start',
                      overflow: 'hidden',
                  },
                  '& .MuiInputBase-input': {
                      overflow: 'auto !important',
                      alignItems: 'flex-start',
                      '&::-webkit-scrollbar': {
                          width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                          background: 'rgba(0,0,0,0.1)',
                          borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                          background: 'rgba(0,0,0,0.3)',
                          borderRadius: '4px',
                          '&:hover': {
                              background: 'rgba(0,0,0,0.4)',
                          },
                      },
                  }
              }}
            />
          </Grid>
        </Grid>

        {/* Image Upload Section */}
        <Box
          sx={(theme) => ({
            mt: 4,
            p: 3,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}05, ${theme.palette.primary.light}10)`,
            border: '1px dashed',
            borderColor: 'primary.main',
            ...theme.applyStyles('dark', {
              background: alpha('#028bcb', 0.08),
              borderColor: alpha('#028bcb', 0.4),
            }),
          })}
        >
          <Typography
            variant="subtitle1"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: 'primary.main',
              fontSize: { xs: '1rem', sm: '1.125rem' },
            }}
          >
            {t('orders.uploadReceipt')}
          </Typography>

          <RtlButton
            variant="outlined"
            component="label"
            startIcon={<AddPhotoAlternateIcon />}
            sx={{
              mb: 2,
              borderRadius: 2,
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
                backgroundColor: 'primary.50',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(2, 139, 203, 0.2)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {t('orders.addReceiptImage')}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleImageChange}
            />
          </RtlButton>

          {/* Image Preview */}
          {receiptImage && (
            <Fade in={!!receiptImage}>
              <Card
                sx={{
                  maxWidth: 400,
                  borderRadius: 2,
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden',
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={URL.createObjectURL(receiptImage)}
                  alt="Receipt Preview"
                  sx={{ objectFit: 'cover' }}
                />
                <CardActions sx={{ justifyContent: 'flex-end', p: 1 }}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={handleRemoveImage}
                    aria-label="delete"
                    sx={{
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        backgroundColor: 'error.light',
                      },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Fade>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={(theme) => ({
          px: 3,
          py: 2.5,
          gap: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          background: theme.palette.background.default,
          ...theme.applyStyles('dark', {
            background: alpha('#0a0a0a', 0.8),
            borderColor: alpha('#ffffff', 0.1),
          }),
        })}
      >
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          sx={(theme) => ({
            borderRadius: 2,
            px: 3,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.08) : 'grey.100',
            },
          })}
        >
          {t('common.cancel')}
        </Button>
        <RtlButton
          type="submit"
          variant="contained"
          loading={isSubmitting}
          sx={{
            background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark}) !important`,
            boxShadow: '0 4px 12px rgba(2, 139, 203, 0.3) !important',
            border: '0 !important',
            borderRadius: 2,
            px: 4,
            py: 1,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            '&:hover': {
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main}) !important`,
              boxShadow: '0 6px 16px rgba(2, 139, 203, 0.4) !important',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          {t('orders.submitPayment')}
        </RtlButton>
      </DialogActions>
    </Dialog>
  );
}

